import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiArrowLeft, FiHelpCircle, FiBookOpen, FiMessageSquare, FiPlus, 
  FiSearch, FiDownload, FiUsers, FiShield, FiCheckCircle, FiArrowRight,
  FiGrid, FiDatabase, FiFileText, FiTool, FiShoppingCart, FiUser
} = FiIcons;

const QuickStartGuide = ({ onNavigate }) => {
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    { id: 'getting-started', title: 'Getting Started', icon: FiHelpCircle },
    { id: 'asking-questions', title: 'Asking Questions', icon: FiMessageSquare },
    { id: 'sharing-learnings', title: 'Sharing Learnings', icon: FiBookOpen },
    { id: 'feedback', title: 'Providing Feedback', icon: FiPlus },
    { id: 'modules', title: 'Using Modules', icon: FiGrid },
    { id: 'search-export', title: 'Search & Export', icon: FiSearch },
    { id: 'roles', title: 'User Roles', icon: FiShield },
    { id: 'workflow', title: 'Question Workflow', icon: FiUsers }
  ];

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'getting-started':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Welcome to PAL-NG Super User Hub</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                PAL-NG Super User Hub is your central knowledge hub for asking questions, sharing learnings, and providing feedback across all maritime operations modules. This platform helps you connect with experts and access collective knowledge.
              </p>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
              <h3 className="font-semibold text-blue-900 mb-2">What you can do:</h3>
              <ul className="text-blue-800 space-y-1">
                <li>• Ask questions and get expert answers</li>
                <li>• Share your knowledge and learnings</li>
                <li>• Provide feedback to improve processes</li>
                <li>• Browse organized content by modules</li>
                <li>• Export data for offline reference</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Dashboard Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiGrid} className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Statistics Cards</span>
                  </div>
                  <p className="text-sm text-gray-600">View total questions, answers, learnings, and feedback. Click "Open Questions" to see questions needing answers.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-green-600" />
                    <span className="font-medium">Quick Actions</span>
                  </div>
                  <p className="text-sm text-gray-600">Three main action buttons to ask questions, share learnings, or submit feedback quickly.</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Navigation</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <SafeIcon icon={FiGrid} className="w-5 h-5 text-purple-600" />
                  <div>
                    <span className="font-medium text-gray-900">Modules</span>
                    <p className="text-sm text-gray-600">Browse content organized by specific areas like Certification, QDMS, QHSE, etc.</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <SafeIcon icon={FiDatabase} className="w-5 h-5 text-purple-600" />
                  <div>
                    <span className="font-medium text-gray-900">Knowledge Base</span>
                    <p className="text-sm text-gray-600">Access answered questions and community learnings in one place.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'asking-questions':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Ask Questions</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Asking good questions helps you get better answers faster. Follow these steps to submit effective questions.
              </p>
            </div>

            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  <h3 className="font-semibold text-gray-900">Click "Ask a Question"</h3>
                </div>
                <p className="text-gray-600 ml-8">Find the blue "Ask a Question" button on the dashboard or in any module page.</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  <h3 className="font-semibold text-gray-900">Write a Clear Title</h3>
                </div>
                <p className="text-gray-600 ml-8">Make your title specific and descriptive. Good: "How to reset password in QDMS?" Bad: "Need help"</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  <h3 className="font-semibold text-gray-900">Select Category & Tags</h3>
                </div>
                <p className="text-gray-600 ml-8">Choose the relevant module and at least one tag to help others find and answer your question.</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">4</span>
                  <h3 className="font-semibold text-gray-900">Provide Details</h3>
                </div>
                <p className="text-gray-600 ml-8">Include context, what you've tried, and what specific help you need. The more details, the better the answers.</p>
              </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
              <h3 className="font-semibold text-green-900 mb-2">Question Status Workflow:</h3>
              <div className="flex items-center gap-2 text-sm text-green-800">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">Open</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full">In Progress</span>
                <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">Answered</span>
              </div>
              <p className="text-green-700 text-sm mt-2">Questions move through these stages as they receive answers and verification.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Tips for Better Questions</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Search existing questions first to avoid duplicates</span>
                </li>
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Include screenshots or error messages when relevant</span>
                </li>
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Be specific about your role and context</span>
                </li>
                <li className="flex items-start gap-2">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Ask follow-up questions if the answer isn't clear</span>
                </li>
              </ul>
            </div>
          </div>
        );

      case 'sharing-learnings':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Sharing Your Learnings</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Share your knowledge and insights to help others. Your learnings can save colleagues time and improve processes across the organization.
              </p>
            </div>

            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  <h3 className="font-semibold text-gray-900">Click "Share a Learning"</h3>
                </div>
                <p className="text-gray-600 ml-8">Find the green "Share a Learning" button on the dashboard or module pages.</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  <h3 className="font-semibold text-gray-900">Choose Learning Type</h3>
                </div>
                <div className="ml-8 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Best Practices</span>
                    <span className="text-sm text-gray-600">Proven methods that work well</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">Process Improvement</span>
                    <span className="text-sm text-gray-600">Ways to make things more efficient</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Learning Tip</span>
                    <span className="text-sm text-gray-600">Quick tips and tricks</span>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  <h3 className="font-semibold text-gray-900">Write Clear Content</h3>
                </div>
                <p className="text-gray-600 ml-8">Explain the learning step-by-step. Include context about when and why to use this knowledge.</p>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">4</span>
                  <h3 className="font-semibold text-gray-900">Add Resources (Optional)</h3>
                </div>
                <p className="text-gray-600 ml-8">Include links to documentation, tools, or additional reading materials that support your learning.</p>
              </div>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
              <h3 className="font-semibold text-blue-900 mb-2">What makes a good learning?</h3>
              <ul className="text-blue-800 space-y-1 text-sm">
                <li>• Practical and actionable advice</li>
                <li>• Real examples from your experience</li>
                <li>• Clear step-by-step instructions</li>
                <li>• Context about when to apply it</li>
                <li>• Links to supporting resources</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Learning Examples</h3>
              <div className="space-y-3">
                <div className="border border-gray-200 rounded-lg p-3">
                  <h4 className="font-medium text-gray-900 mb-1">Good Example:</h4>
                  <p className="text-sm text-gray-600">"How to quickly find expired certificates in QDMS: Use the advanced filter with expiry date range, then export results for follow-up actions."</p>
                </div>
                <div className="border border-red-200 rounded-lg p-3 bg-red-50">
                  <h4 className="font-medium text-red-900 mb-1">Avoid:</h4>
                  <p className="text-sm text-red-700">"QDMS is confusing" (too vague, not helpful)</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'feedback':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Providing Feedback</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Your feedback helps improve systems and processes. Share suggestions, report issues, and highlight pain points to make everyone's work easier.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Types of Feedback</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs rounded-full">Suggestion</span>
                  </div>
                  <p className="text-sm text-gray-600">Ideas for new features or improvements to existing processes.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Bug</span>
                  </div>
                  <p className="text-sm text-gray-600">Technical issues or errors you've encountered in systems.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full">Pain Point</span>
                  </div>
                  <p className="text-sm text-gray-600">Processes or workflows that are difficult or time-consuming.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-violet-100 text-violet-800 text-xs rounded-full">UI/UX</span>
                  </div>
                  <p className="text-sm text-gray-600">Interface design issues or user experience problems.</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Feedback Sources</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded-full">Personal</span>
                  <span className="text-sm text-gray-700">Issues you've experienced personally in your daily work</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">Ship</span>
                  <span className="text-sm text-gray-700">Feedback from ship operations or crew members</span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">How to Submit Feedback</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-bold mt-1">1</span>
                  <div>
                    <h4 className="font-medium text-gray-900">Be Specific</h4>
                    <p className="text-sm text-gray-600">Describe exactly what the issue is, when it happens, and what you expected instead.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-bold mt-1">2</span>
                  <div>
                    <h4 className="font-medium text-gray-900">Provide Context</h4>
                    <p className="text-sm text-gray-600">Include your role, the system/process involved, and the impact on your work.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-bold mt-1">3</span>
                  <div>
                    <h4 className="font-medium text-gray-900">Suggest Solutions</h4>
                    <p className="text-sm text-gray-600">If you have ideas for fixes or improvements, include them in your feedback.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-r-lg">
              <h3 className="font-semibold text-orange-900 mb-2">Feedback Voting</h3>
              <p className="text-orange-800 text-sm">
                You can upvote feedback from others that you agree with or have experienced similar issues. 
                This helps prioritize the most common problems and suggestions.
              </p>
            </div>
          </div>
        );

      case 'modules':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Using Modules</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Content is organized into modules based on different areas of maritime operations. Each module contains questions, learnings, and feedback specific to that area.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Available Modules</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiShield} className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Certification</span>
                  </div>
                  <p className="text-sm text-gray-600">Training, certifications, and compliance requirements.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiDatabase} className="w-5 h-5 text-green-600" />
                    <span className="font-medium">QDMS</span>
                  </div>
                  <p className="text-sm text-gray-600">Quality and Document Management System.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiShield} className="w-5 h-5 text-red-600" />
                    <span className="font-medium">QHSE</span>
                  </div>
                  <p className="text-sm text-gray-600">Quality, Health, Safety, and Environment.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiFileText} className="w-5 h-5 text-purple-600" />
                    <span className="font-medium">Forms & Checklists</span>
                  </div>
                  <p className="text-sm text-gray-600">Standard forms and operational checklists.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiShoppingCart} className="w-5 h-5 text-orange-600" />
                    <span className="font-medium">Procurement</span>
                  </div>
                  <p className="text-sm text-gray-600">Purchasing, suppliers, and procurement processes.</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiTool} className="w-5 h-5 text-yellow-600" />
                    <span className="font-medium">Maintenance</span>
                  </div>
                  <p className="text-sm text-gray-600">Equipment maintenance and repair procedures.</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Module Features</h3>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Organized Content</h4>
                  <p className="text-sm text-gray-600 mb-3">Each module shows three tabs: Questions, Learnings, and Feedback specific to that area.</p>
                  <div className="flex gap-2 text-xs">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full">Questions Tab</span>
                    <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full">Learnings Tab</span>
                    <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded-full">Feedback Tab</span>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Quick Actions</h4>
                  <p className="text-sm text-gray-600">Each module page has action buttons to quickly submit content for that specific module.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Module-Specific Search</h4>
                  <p className="text-sm text-gray-600">Search and filter content within each module to find exactly what you're looking for.</p>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 border-l-4 border-purple-400 p-4 rounded-r-lg">
              <h3 className="font-semibold text-purple-900 mb-2">Tip: Choose the Right Module</h3>
              <p className="text-purple-800 text-sm">
                Selecting the correct module helps others find your content more easily. If you're unsure, 
                use the "General" module or ask in the most relevant one.
              </p>
            </div>
          </div>
        );

      case 'search-export':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Search & Export Features</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Find information quickly with powerful search and filtering options, then export data for offline use or sharing.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Search Options</h3>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiSearch} className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Text Search</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">Search through titles and content of questions, learnings, and feedback.</p>
                  <div className="bg-gray-50 p-2 rounded text-xs text-gray-700">
                    Example: Search "password reset" to find all content about resetting passwords
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiGrid} className="w-5 h-5 text-green-600" />
                    <span className="font-medium">Category Filter</span>
                  </div>
                  <p className="text-sm text-gray-600">Filter by specific modules like QDMS, QHSE, Certification, etc.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiHelpCircle} className="w-5 h-5 text-purple-600" />
                    <span className="font-medium">Status Filter</span>
                  </div>
                  <p className="text-sm text-gray-600">For questions: filter by Open, In Progress, or Answered status.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiBookOpen} className="w-5 h-5 text-orange-600" />
                    <span className="font-medium">Tag Filter</span>
                  </div>
                  <p className="text-sm text-gray-600">Use tags like "Workflow", "Best Practices", or "Process Improvement" for specific topics.</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Export to Excel</h3>
              <div className="space-y-4">
                <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
                  <h4 className="font-semibold text-green-900 mb-2">What gets exported:</h4>
                  <ul className="text-green-800 space-y-1 text-sm">
                    <li>• All filtered results (only what you see on screen)</li>
                    <li>• Complete data including descriptions and metadata</li>
                    <li>• Export information sheet with filters applied</li>
                    <li>• Properly formatted Excel file with auto-sized columns</li>
                  </ul>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Export Features</h4>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p>✅ <strong>Browser-based:</strong> No server storage, files download directly</p>
                    <p>✅ <strong>Filter-aware:</strong> Only exports currently visible/filtered data</p>
                    <p>✅ <strong>Timestamped files:</strong> Easy to organize multiple exports</p>
                    <p>✅ <strong>Multiple sheets:</strong> Data + metadata in organized tabs</p>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">How to Export</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                      <span>Apply your desired filters and search terms</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                      <span>Click the green "Export to Excel" button</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                      <span>File downloads automatically to your device</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Search Tips</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium text-green-700">✅ Good Searches:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• "certificate expiry"</li>
                    <li>• "QDMS access"</li>
                    <li>• "safety checklist"</li>
                    <li>• "procurement process"</li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <h4 className="font-medium text-red-700">❌ Less Effective:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• "help"</li>
                    <li>• "problem"</li>
                    <li>• "how"</li>
                    <li>• Single letters or numbers</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'roles':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">User Roles & Permissions</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Different user roles have different capabilities in the system. Your role is automatically assigned based on your email domain.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Role Types</h3>
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">Maersk Team</span>
                    <span className="text-sm text-gray-600">@maersk.com</span>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-700 font-medium">Capabilities:</p>
                    <ul className="text-sm text-gray-600 space-y-1 ml-4">
                      <li>• Full access to all modules</li>
                      <li>• Can moderate content</li>
                      <li>• Can manage users</li>
                      <li>• Can verify answers as correct</li>
                      <li>• Answers are automatically marked as expert answers</li>
                    </ul>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">Mariapps SME</span>
                    <span className="text-sm text-gray-600">@mariapps.com</span>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-700 font-medium">Capabilities:</p>
                    <ul className="text-sm text-gray-600 space-y-1 ml-4">
                      <li>• Full access to all modules</li>
                      <li>• Can moderate content</li>
                      <li>• Can verify answers as correct</li>
                      <li>• Answers are automatically marked as expert answers</li>
                      <li>• Subject matter expert for technical questions</li>
                    </ul>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">Super User</span>
                    <span className="text-sm text-gray-600">@maerskcrew.com</span>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-700 font-medium">Capabilities:</p>
                    <ul className="text-sm text-gray-600 space-y-1 ml-4">
                      <li>• Full access to all modules</li>
                      <li>• Can ask questions and share learnings</li>
                      <li>• Can provide feedback</li>
                      <li>• Can answer questions</li>
                      <li>• Answers require verification by SMEs</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Answer Verification Process</h3>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">Regular User Answer</span>
                    <span className="text-sm text-gray-600">→ Needs verification by SME</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Expert Answer</span>
                    <span className="text-sm text-gray-600">→ Automatically verified</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Verified Answer</span>
                    <span className="text-sm text-gray-600">→ Marked as correct solution</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Access Control</h3>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                <h4 className="font-semibold text-yellow-900 mb-2">Domain-Based Authentication</h4>
                <p className="text-yellow-800 text-sm mb-2">
                  Only users with authorized email domains can access the system:
                </p>
                <ul className="text-yellow-800 text-sm space-y-1">
                  <li>• @maersk.com</li>
                  <li>• @maerskcrew.com</li>
                  <li>• @mariapps.com</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Role Indicators</h3>
              <p className="text-gray-600 text-sm mb-3">You can identify user roles by the badges shown on their content:</p>
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">Maersk Team</span>
                  <span className="text-sm text-gray-600">Highest authority, can moderate and manage</span>
                </div>
                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Mariapps SME</span>
                  <span className="text-sm text-gray-600">Technical expert, trusted answers</span>
                </div>
                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Super User</span>
                  <span className="text-sm text-gray-600">Internal team member</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'workflow':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Question Status Workflow</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Questions move through different statuses as they receive answers and verification. Understanding this workflow helps you track progress and know when questions are fully resolved.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Status Flow</h3>
              <div className="space-y-4">
                <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">Open</span>
                    <span className="text-sm text-gray-600">Initial status</span>
                  </div>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Question has been submitted</li>
                    <li>• Waiting for answers from the community</li>
                    <li>• Extension questions can be added</li>
                    <li>• Appears in "Open Questions" filter</li>
                  </ul>
                </div>

                <div className="flex justify-center">
                  <SafeIcon icon={FiArrowRight} className="w-6 h-6 text-gray-400" />
                </div>

                <div className="border border-yellow-200 rounded-lg p-4 bg-yellow-50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">In Progress</span>
                    <span className="text-sm text-gray-600">Has answers, pending verification</span>
                  </div>
                  <ul className="text-sm text-yellow-800 space-y-1">
                    <li>• At least one answer has been submitted</li>
                    <li>• Answers are awaiting verification by experts</li>
                    <li>• More answers can still be added</li>
                    <li>• Extension questions no longer allowed</li>
                  </ul>
                </div>

                <div className="flex justify-center">
                  <SafeIcon icon={FiArrowRight} className="w-6 h-6 text-gray-400" />
                </div>

                <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">Answered</span>
                    <span className="text-sm text-gray-600">Verified solution available</span>
                  </div>
                  <ul className="text-sm text-green-800 space-y-1">
                    <li>• At least one answer has been verified by an expert</li>
                    <li>• Question is considered resolved</li>
                    <li>• Appears in Knowledge Base</li>
                    <li>• Additional answers can still be added</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Status Triggers</h3>
              <div className="space-y-3">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Open → In Progress</h4>
                  <p className="text-sm text-gray-600">Automatically triggered when any user submits an answer to the question.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">In Progress → Answered</h4>
                  <p className="text-sm text-gray-600">Triggered when:</p>
                  <ul className="text-sm text-gray-600 mt-1 ml-4 space-y-1">
                    <li>• An expert (SME or Mariapps SME) submits an answer (automatic)</li>
                    <li>• An expert verifies an existing answer as correct</li>
                  </ul>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Direct to Answered</h4>
                  <p className="text-sm text-gray-600">When an expert submits the first answer, the question skips "In Progress" and goes directly to "Answered".</p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Extension Questions</h3>
              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded-r-lg">
                <h4 className="font-semibold text-orange-900 mb-2">When Available</h4>
                <p className="text-orange-800 text-sm mb-2">
                  Extension questions can only be added when a question has "Open" status. This allows for:
                </p>
                <ul className="text-orange-800 text-sm space-y-1">
                  <li>• Follow-up questions related to the main topic</li>
                  <li>• Clarification requests</li>
                  <li>• Additional context that might help answerers</li>
                </ul>
                <p className="text-orange-800 text-sm mt-2">
                  Once answers start coming in (In Progress status), the focus shifts to answering rather than expanding the question.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Answer Verification</h3>
              <div className="space-y-3">
                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">Pending Verification</span>
                  </div>
                  <p className="text-sm text-gray-600">Regular user answers that need expert review.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Expert Answer</span>
                  </div>
                  <p className="text-sm text-gray-600">Answers from SMEs or Mariapps SMEs, automatically trusted.</p>
                </div>

                <div className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Verified Answer</span>
                  </div>
                  <p className="text-sm text-gray-600">Answers that have been reviewed and approved by an expert.</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => onNavigate('dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors duration-200"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="flex items-center gap-3 mb-2">
          <SafeIcon icon={FiHelpCircle} className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Quick Start Guide</h1>
        </div>
        <p className="text-gray-600">Learn how to use PAL-NG effectively and get the most out of the platform</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar Navigation */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="lg:col-span-1"
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sticky top-6">
            <h3 className="font-semibold text-gray-900 mb-4">Guide Sections</h3>
            <nav className="space-y-2">
              {sections.map((section, index) => (
                <motion.button
                  key={section.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + index * 0.05 }}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 ${
                    activeSection === section.id
                      ? 'bg-blue-50 border-blue-200 text-blue-700 border'
                      : 'hover:bg-gray-50 text-gray-700 border border-transparent'
                  }`}
                >
                  <SafeIcon icon={section.icon} className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium">{section.title}</span>
                </motion.button>
              ))}
            </nav>
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-3"
        >
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSection}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {renderSectionContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions Footer */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-8 bg-gradient-to-r from-blue-50 to-green-50 border border-gray-200 rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Ready to get started?</h3>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => onNavigate('question-form')}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiHelpCircle} className="w-5 h-5" />
            Ask Your First Question
          </button>
          <button
            onClick={() => onNavigate('learning-form')}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiBookOpen} className="w-5 h-5" />
            Share a Learning
          </button>
          <button
            onClick={() => onNavigate('feedback-form')}
            className="flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
          >
            <SafeIcon icon={FiMessageSquare} className="w-5 h-5" />
            Submit Feedback
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default QuickStartGuide;